import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useSelector } from 'react-redux';

import { store } from 'redux/store';
import {
  resetCP,
  selectDVCPMATrackingForecastSubpage,
} from 'redux/reducers/meteorologyAgroTrackingForecastDetailedViewControlPanelReducer';
import { SUBPAGE_NAMES } from 'types/Meteorology/Agro/TrackingForecast/DetailedView';

import useControls from '../useControls';
import useChartRequestTrigger from '../useChartRequestTrigger';
import useSavePreference from '../useSavePreference';
import useSelectPreference from '../useSelectPreference';
import useListPreferences from '../useListPreferences';
import useDeletePreference from '../useDeletePreference';
import useGroupedLocations from '../useGroupedLocations';
import useTimeFrequency from '../useTimeFrequency';
import useVariables from '../useVariables';
import useForecastDate from '../useForecastDate';
import useRounds from '../useRounds';

/**
 * Agrega o estado da Visão Detalhada do Agro para a versão mobile, espelhando
 * `hooks/Meteorology/Fields/TrackingForecast/DetailedView/Mobile/useDetailedViewMobile`.
 * O Agro tem só 2 subpages (`locations`/`rounds`) — não há modelo nem membro aqui.
 */
export default function useDetailedViewMobile() {
  const subpage = useSelector(selectDVCPMATrackingForecastSubpage);
  const { subpageButtonProps, chartMode, setChartMode } = useControls();
  const triggerRequest = useChartRequestTrigger();
  const savePreference = useSavePreference();
  const { selectPreference } = useSelectPreference();
  const { preferences } = useListPreferences();
  const { preferenceDeleteTrigger } = useDeletePreference();
  const groupedLocations = useGroupedLocations();
  const timeFrequency = useTimeFrequency();
  const variables = useVariables();
  const forecastDate = useForecastDate();
  const rounds = useRounds();

  const clearSelection = () => store.dispatch(resetCP());

  const variableLabel = variables.variables.find(
    (v) => v.value === variables.selectedVariable,
  )?.label;
  const dateLabel = forecastDate.selectedDate
    ? format(forecastDate.selectedDate, 'dd/MM/yyyy', { locale: ptBR })
    : undefined;

  const chips: string[] = [];
  if (timeFrequency.selectedTimeFrequency) chips.push(timeFrequency.selectedTimeFrequency.label);
  if (variableLabel) chips.push(variableLabel);
  if (subpage === 'locations') {
    if (groupedLocations.selectedMultiLocations.length) {
      chips.push(`${groupedLocations.selectedMultiLocations.length} locais`);
    }
    if (dateLabel) chips.push(dateLabel);
  } else if (subpage === 'rounds') {
    if (groupedLocations.selectedLocation) chips.push(groupedLocations.selectedLocation.name);
    if (rounds.selectedRounds.length) chips.push(`${rounds.selectedRounds.length} rodadas`);
  }

  return {
    subpage,
    subpageLabel: SUBPAGE_NAMES[subpage],
    subpageButtonProps,
    chartMode,
    setChartMode,
    chips: chips.slice(0, 4),
    triggerRequest,
    clearSelection,
    savePreference,
    selectPreference,
    preferences,
    preferenceDeleteTrigger,
    groupedLocations,
    timeFrequency,
    variables,
    forecastDate,
    rounds,
  };
}
